import { formatTime } from "@clipboard-health/date-time";
import { isDefined } from "@clipboard-health/util-ts";
import { FacilityRoles, FacilityUserPermissions } from "@src/appV2/FacilityUser/types";
import { differenceInMinutes } from "date-fns";

import { type ShiftChargeRateInfo } from "./hooks/useShiftChargeRates";
import { type ShiftQualificationPricingSettings } from "./types";

export const ChargeUpdateKind = {
  // Only offer VC
  VariableCharge: "VariableCharge",
  // Only offer W&S
  WaitAndSave: "WaitAndSave",
  // The user may choose either VC or W&S
  Either: "Either",
  // The data has not yet loaded
  Loading: "Loading",
} as const;

// eslint-disable-next-line @typescript-eslint/no-redeclare
export type ChargeUpdateKind = (typeof ChargeUpdateKind)[keyof typeof ChargeUpdateKind];

export function getChargeUpdateKind(parameters: {
  waitAndSaveEnabled: boolean | undefined;
  shiftChargeRates: ShiftChargeRateInfo[];
  shift: { start: string } | undefined;
}): ChargeUpdateKind {
  const { waitAndSaveEnabled, shiftChargeRates, shift } = parameters;

  if (!waitAndSaveEnabled) {
    return ChargeUpdateKind.VariableCharge;
  }

  if (!isDefined(shift)) {
    return ChargeUpdateKind.Loading;
  }

  const { start } = shift;

  const hasManualRateOverride = shiftChargeRates.some((rate) =>
    isDefined(rate.manualRateInMinorUnits)
  );

  if (hasManualRateOverride) {
    return ChargeUpdateKind.VariableCharge;
  }

  const activeWaitAndSaveExists = shiftChargeRates.some((info) =>
    isDefined(info.waitAndSaveAuction)
  );

  if (activeWaitAndSaveExists) {
    return ChargeUpdateKind.WaitAndSave;
  }

  if (!isDefined(start) || differenceInMinutes(new Date(start), new Date()) <= 60) {
    // If the shift starts in less than 1 hour, Smart pricing will price it at
    // the maximum charge rate anyway, so it doesn't make sense to use W&S and
    // we just go for a flat rate.
    return ChargeUpdateKind.VariableCharge;
  }

  return ChargeUpdateKind.Either;
}

interface User {
  access?: string[];
  permissions?: string[];
}

export function canUserEditShiftRates(user: User | undefined, isAdmin: boolean) {
  const userHasAdminRole = user?.access?.includes(FacilityRoles.ADMIN) ?? false;
  const userHasEditShiftRatePermission =
    user?.permissions?.includes(FacilityUserPermissions.EDIT_SHIFT_RATE) ?? false;
  return isAdmin || userHasAdminRole || userHasEditShiftRatePermission;
}

export function getSetPricingSettingsRequestData(parameters: {
  modifiedChargeRates: Record<string, number>;
  shiftChargeRates: ShiftChargeRateInfo[];
  description: string;
}) {
  const { modifiedChargeRates, shiftChargeRates, description } = parameters;

  return Object.entries(modifiedChargeRates).map(([qualification, amountInMinorUnits]) => {
    const chargeRateInfo = shiftChargeRates.find((info) => info.qualification === qualification);
    const defaultRate = chargeRateInfo?.constraints.minimumAmountInMinorUnits;

    const waitAndSaveAuction =
      amountInMinorUnits === defaultRate
        ? null
        : {
            maximumChargeInMinorUnits: amountInMinorUnits,
            startAt: chargeRateInfo?.waitAndSaveAuction?.startAt ?? new Date().toISOString(),
          };

    return {
      qualification,
      description,
      existingAuctionSettingsId: chargeRateInfo?.existingPricingSettingsId,
      waitAndSaveAuction,
      manualChargeInMinorUnits: null,
    };
  });
}

export function getManualChargePricingSettingsRequestData(parameters: {
  modifiedChargeRates: Record<string, number>;
  shiftChargeRates: ShiftChargeRateInfo[];
  description: string;
  clearWaitAndSave?: boolean;
}) {
  const { modifiedChargeRates, shiftChargeRates, description, clearWaitAndSave } = parameters;

  return Object.entries(modifiedChargeRates).map(([qualification, amountInMinorUnits]) => {
    const chargeRateInfo = shiftChargeRates.find((info) => info.qualification === qualification);
    const defaultRate = chargeRateInfo?.defaultRateInMinorUnits;

    const manualChargeInMinorUnits = amountInMinorUnits === defaultRate ? null : amountInMinorUnits;

    return {
      qualification,
      description,
      existingAuctionSettingsId: chargeRateInfo?.existingPricingSettingsId,
      waitAndSaveAuction: clearWaitAndSave === true ? null : undefined,
      manualChargeInMinorUnits,
    };
  });
}

export function getPricingSettingsId(shiftId: string, qualification: string) {
  return `${shiftId}:${qualification}`;
}

export function moneyMinorUnitsToMajorUnits(minorUnits: number): number {
  return minorUnits / 100;
}

export function moneyMajorUnitsToMinorUnits(dollars: number): number {
  return Math.round(dollars * 100);
}

export interface ConcurrentChargeKindAnalysis {
  uniformKind: typeof ChargeUpdateKind.VariableCharge | typeof ChargeUpdateKind.WaitAndSave | null;
  isMixed: boolean;
}

/**
 * Analyzes all concurrent shifts (primary + others) to determine whether they
 * agree on a single charge kind or have a mix of flat-rate and wait-and-save.
 *
 * Shifts with neither manual charge nor wait-and-save auction are "neutral" and
 * do not contribute to the determination. Manual charge takes precedence over
 * wait-and-save (matching `getChargeUpdateKind` priority).
 */
export function getConcurrentChargeKindAnalysis(parameters: {
  settingsByShiftId: Record<string, ShiftQualificationPricingSettings[]>;
  primaryShiftChargeRates: ShiftChargeRateInfo[];
}): ConcurrentChargeKindAnalysis {
  const { settingsByShiftId, primaryShiftChargeRates } = parameters;

  let hasManual = false;
  let hasWaitAndSave = false;

  // Check primary shift
  const primaryHasManual = primaryShiftChargeRates.some((rate) =>
    isDefined(rate.manualRateInMinorUnits)
  );
  const primaryHasWaitAndSave = primaryShiftChargeRates.some((rate) =>
    isDefined(rate.waitAndSaveAuction)
  );

  if (primaryHasManual) {
    hasManual = true;
  } else if (primaryHasWaitAndSave) {
    hasWaitAndSave = true;
  }

  // Check other concurrent shifts
  for (const settings of Object.values(settingsByShiftId)) {
    const shiftHasManual = settings.some((s) => isDefined(s.attributes.manualChargeInMinorUnits));
    const shiftHasWaitAndSave = settings.some((s) => isDefined(s.attributes.waitAndSaveAuction));

    // Manual takes precedence (matches getChargeUpdateKind priority)
    if (shiftHasManual) {
      hasManual = true;
    } else if (shiftHasWaitAndSave) {
      hasWaitAndSave = true;
    }
  }

  const isMixed = hasManual && hasWaitAndSave;

  if (isMixed) {
    return { uniformKind: null, isMixed: true };
  }

  if (hasWaitAndSave) {
    return { uniformKind: ChargeUpdateKind.WaitAndSave, isMixed: false };
  }

  if (hasManual) {
    return { uniformKind: ChargeUpdateKind.VariableCharge, isMixed: false };
  }

  // All shifts are neutral (no explicit charge kind set)
  return { uniformKind: null, isMixed: false };
}

export function getApplyToAllDescription(parameters: {
  shift: { start: string; end: string };
  workerRequirement: string;
  concurrentShiftCount: number;
  timeZone?: string;
}): string {
  const { shift, workerRequirement, concurrentShiftCount, timeZone } = parameters;

  if (!timeZone) {
    return `all ${concurrentShiftCount} unfilled ${workerRequirement} shifts in this slot`;
  }

  const startTime = formatTime(shift.start, { timeZone });
  const endTime = formatTime(shift.end, { timeZone });

  return `all ${concurrentShiftCount} unfilled ${workerRequirement} shifts from ${startTime} to ${endTime} on this day`;
}

export function getEffectiveChargeUpdateKind(parameters: {
  primaryChargeUpdateKind: ChargeUpdateKind;
  concurrentAnalysis: ConcurrentChargeKindAnalysis;
  hasMultipleQualifications: boolean;
  applyToAll: boolean;
}): {
  effectiveKind: ChargeUpdateKind;
  applyToAllDisabled: boolean;
} {
  const { primaryChargeUpdateKind, concurrentAnalysis, hasMultipleQualifications, applyToAll } =
    parameters;

  const applyToAllDisabled = concurrentAnalysis.isMixed && hasMultipleQualifications;

  // When applying to all and all shifts agree, override the primary kind.
  // Useful when the primary is "Either" but all concurrent shifts already
  // have a specific charge kind set.
  const effectiveKind =
    applyToAll && !concurrentAnalysis.isMixed && concurrentAnalysis.uniformKind !== null
      ? concurrentAnalysis.uniformKind
      : primaryChargeUpdateKind;

  return { effectiveKind, applyToAllDisabled };
}
